'use client'
import { productCardHelper } from '@/components/common/product-card/helper'
import { TrackingDataLayer } from '@/components/extension/tracking-data-layer'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useSearchProductsQuery } from '@/hooks/react-query/products/use-search-products'
import { useMobileNav } from '@/hooks/zustands/use-mobile-nav'
import { useSearchProductsStore } from '@/hooks/zustands/use-search-products'
import { ETrackingEvent } from '@/lib/gtm/type'
import { cn } from '@/lib/utils'
import { useSettingData } from '@/providers/setting-provider'
import type { Product } from '@/types/product'
import { liteClient as algoliasearch } from 'algoliasearch/lite'
import type { Hit } from 'instantsearch.js'
import { Search, SearchIcon, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useEffect, useRef } from 'react'
import {
  Configure,
  Highlight,
  Index,
  InstantSearch,
  useHits,
  useInstantSearch,
  useSearchBox,
} from 'react-instantsearch'
import { useBoolean, useDebounceValue } from 'usehooks-ts'
import {
  FeaturedCollections,
  filterFeaturedCollectionsByKeyword,
  getFeaturedCollectionKeywords,
  getVisibleFeaturedCollections,
} from './featured-collections'
import { RecentProducts } from './recent-products'
import {
  saveSearchHighlightProducts,
  type SearchHighlightProduct,
} from './search-highlight-storage'
import { SearchProductCard } from './search-product-card'

// Define Algolia client
const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || 'APP_ID_HERE',
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY || 'SEARCH_KEY_HERE'
)

type SearchResultProduct = Product & {
  image?: string
  slug?: string
  objectID?: string
  __position?: number
  __queryID?: string
}

const isAlgoliaHitProduct = (
  product: SearchResultProduct
): product is Hit<SearchResultProduct> => {
  return (
    typeof product.objectID === 'string' &&
    typeof product.__position === 'number'
  )
}

const toSearchHighlightProduct = (
  product: SearchResultProduct
): SearchHighlightProduct => {
  const { regular_price, compare_price, sale_percent } =
    productCardHelper.getPrices(product)
  return {
    id: String(product.id ?? product.objectID ?? ''),
    title: product.title,
    href: `/product/${product.slug ?? product.seo_alias ?? '#'}`,
    image: product.image || product.images?.[0]?.src || '',
    spu: product.spu,
    icon_thumbnail: product.icon_thumbnail,
    regular_price,
    compare_price,
    sale_percent,
  }
}

export function SearchBar({ isMobile }: { isMobile?: boolean }) {
  const t = useTranslations('header.search_bar')
  const { value: open, setTrue, setFalse } = useBoolean(false)
  const { keyword } = useSearchProductsStore()
  const [searchTerm] = useDebounceValue(keyword, 500)

  return (
    <>
      <div onClick={setTrue}>
        {isMobile ? (
          <span className='flex h-13 w-10.5 cursor-pointer items-center justify-center'>
            <Search size={20} />
          </span>
        ) : (
          <div className='relative cursor-pointer'>
            <Input
              value={searchTerm}
              placeholder={t('placeholder.search_product') + '...'}
              className='h-12 cursor-pointer rounded-full bg-white py-2 pr-4 md:pr-9'
              readOnly
              ga-tracking-value={`${isMobile ? 'mb' : ''}navbar__search-bar`}
              ga-tracking-label='search_product'
            />
            <SearchIcon className='absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-500' />
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setFalse}>
        <DialogContent
          showCloseButton={false}
          className='top-0 flex max-h-dvh max-w-full translate-y-0 flex-col gap-2 bg-transparent p-0'
        >
          <InstantSearch
            searchClient={searchClient}
            indexName={
              process.env.NEXT_PUBLIC_ALGOLIA_PRODUCT_INDEX || 'products'
            }
            insights={true}
            stalledSearchDelay={200}
          >
            <Configure hitsPerPage={4} clickAnalytics={true} />
            <SearchMain isMobile={isMobile} setFalse={setFalse} />
          </InstantSearch>
        </DialogContent>
      </Dialog>
    </>
  )
}

const SearchMain = ({
  isMobile,
  setFalse,
}: {
  isMobile?: boolean
  setFalse: () => void
}) => {
  const homeSetting = useSettingData()
  const t = useTranslations('header.search_bar')

  const { keyword, setKeyword } = useSearchProductsStore()
  const [searchTerm, setSearchTerm] = useDebounceValue(keyword, 200)

  const inputRef = useRef<HTMLInputElement>(null)
  const dialogContentRef = useRef<HTMLDivElement>(null)
  const viewAllRef = useRef<HTMLAnchorElement>(null)

  const router = useRouter()
  const { setIsOpen } = useMobileNav()
  const { refine } = useSearchBox()

  // Sync debounce term to Algolia for optimized search requests
  useEffect(() => {
    refine(searchTerm)
  }, [searchTerm, refine])

  const isSearching = searchTerm.trim().length > 0
  const featuredCollections = homeSetting?.search_featured_collections
  const featuredCollectionItems =
    getVisibleFeaturedCollections(featuredCollections)
  // Chỉ lấy từ khoá của các BST đang hiển thị, đứng trước từ khoá chung, bỏ trùng
  const getPopularCategories = (
    collectionItems: typeof featuredCollectionItems,
    withGeneralKeywords: boolean
  ) =>
    Array.from(
      new Set(
        [
          ...getFeaturedCollectionKeywords(collectionItems),
          ...(withGeneralKeywords
            ? (homeSetting?.keyword?.split(',') ?? [])
            : []),
        ]
          .map((keyword) => keyword.trim())
          .filter(Boolean)
      )
    )

  const goToSpotlight = (term: string) => {
    router.push(`/spotlight?search=${encodeURIComponent(term)}`)
    setFalse()
    setTimeout(() => {
      setIsOpen(false)
    }, 300)
  }

  // Enter / phím Tìm trên iOS tương đương bấm CTA "Xem tất cả"
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    inputRef.current?.blur()

    const term = keyword.trim()
    if (!term) return

    // CTA đang hiển thị đúng từ khoá -> bấm hộ để giữ nguyên lưu highlight + tracking
    if (viewAllRef.current && term === searchTerm.trim()) {
      viewAllRef.current.click()
      return
    }

    // Chưa có CTA (đang tải / chưa qua debounce) -> điều hướng thẳng
    goToSpotlight(term)
  }

  // Từ khoá phổ biến + gợi ý tìm kiếm: chuyển thẳng sang trang tìm kiếm
  const handleCategoryClick = (category: string) => {
    setKeyword(category)
    goToSpotlight(category)
  }

  const matchedFeaturedCollectionItems = isSearching
    ? filterFeaturedCollectionsByKeyword(featuredCollectionItems, searchTerm)
    : []

  // BST nổi bật + từ khoá nổi bật: dùng khi chưa search và khi search không có kết quả
  // (case không có kết quả chỉ hiện từ khoá của BST, ẩn từ khoá chung)
  const renderCategories = (
    collectionItems: typeof featuredCollectionItems,
    withGeneralKeywords = true
  ) => {
    const popularCategories = getPopularCategories(
      collectionItems,
      withGeneralKeywords
    )

    return (
      <div className='mb-4 space-y-4'>
        <FeaturedCollections
          title={featuredCollections?.title}
          items={collectionItems}
          setFalse={setFalse}
        />

        {/* Popular keywords */}
        {popularCategories.length > 0 ? (
          <div>
            <p className='mb-4 text-base font-medium'>
              {t('title.popular_keywords')}
            </p>
            <div className='flex flex-nowrap gap-2 overflow-x-auto pb-3'>
              {popularCategories.map((category) => (
                <Button
                  key={category}
                  className={cn(
                    'rounded-full border border-neutral-200 px-4 py-2 font-sans text-sm normal-case',
                    keyword === category && 'bg-neutral-900 text-neutral'
                  )}
                  variant='outline-neutral'
                  size='small'
                  onClick={() => handleCategoryClick(category)}
                  ga-tracking-value={`navbar__search__kw`}
                  ga-tracking-label={`navbar__search__kw__${category}`}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <>
      <DialogTitle className='hidden'></DialogTitle>
      <div className='flex items-center justify-center gap-4 bg-white p-4 md:gap-8'>
        <form className='relative max-w-96 flex-1' onSubmit={handleSubmit}>
          <Input
            ref={inputRef}
            type='text'
            inputMode='search'
            enterKeyHint='search'
            autoCapitalize='off'
            autoCorrect='off'
            value={keyword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearchTerm(e.target.value)
              setKeyword(e.target.value)
            }}
            placeholder={t('placeholder.search_product') + '...'}
            className='h-12 appearance-none rounded-full bg-gray-100 pl-10 pr-16 shadow-none focus-visible:ring-0'
            autoFocus
            ga-tracking-value={`${isMobile ? 'mb' : ''}navbar__search-bar__active`}
            ga-tracking-label='search_product_active'
          />
          <SearchIcon className='absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-500' />
          {keyword ? (
            <button
              type='button'
              onClick={() => {
                setSearchTerm('')
                setKeyword('')
                inputRef.current?.focus()
              }}
              className='absolute right-4 top-1/2 -translate-y-1/2 text-sm text-neutral-600 hover:text-neutral-900'
            >
              {t('clear_search')}
            </button>
          ) : null}
        </form>
        <button onClick={setFalse} aria-label={t('close_search')}>
          <X className='h-6 w-6 text-gray-500 md:h-8 md:w-8' />
        </button>
      </div>

      {/* Ô search đứng yên, phần kết quả cuộn trong giới hạn màn hình */}
      <div className='min-h-0 overflow-y-auto overscroll-contain border-none pb-4'>
        <div
          ref={dialogContentRef}
          className='mx-auto max-w-[calc(100%-20px)] overflow-hidden rounded-lg bg-white px-5 py-10 md:px-20 lg:max-w-5xl'
        >
          {/* Categories */}
          {!isSearching ? renderCategories(featuredCollectionItems) : null}

          {isSearching ? (
            <div className='flex flex-col gap-6 lg:gap-8'>
              <FeaturedCollections
                title={featuredCollections?.title}
                items={matchedFeaturedCollectionItems}
                setFalse={setFalse}
              />
              <QuerySuggestions onSelectSuggestion={handleCategoryClick} />
              <SearchResultProducts
                keyword={searchTerm}
                setFalse={setFalse}
                viewAllRef={viewAllRef}
                // BST đã hiện ở trên (khớp từ khoá) thì không lặp lại
                emptyContent={renderCategories(
                  featuredCollectionItems.filter(
                    (item) => !matchedFeaturedCollectionItems.includes(item)
                  ),
                  false
                )}
              />
            </div>
          ) : (
            <RecentProducts setFalse={setFalse} />
          )}
        </div>
      </div>
    </>
  )
}

const SearchResultProducts = ({
  keyword,
  setFalse,
  emptyContent,
  viewAllRef,
}: {
  keyword: string
  setFalse: () => void
  emptyContent?: React.ReactNode
  viewAllRef?: React.Ref<HTMLAnchorElement>
}) => {
  const { setIsOpen } = useMobileNav()
  const t = useTranslations()

  const { hits } = useHits<SearchResultProduct>()
  const { status } = useInstantSearch({ catchError: true })

  const hasAlgoliaHits = hits.length > 0
  const shouldUseFallback =
    keyword.trim().length > 0 &&
    (status === 'error' || (status === 'idle' && !hasAlgoliaHits))

  const {
    data: fallbackData,
    isLoading: isFallbackLoading,
    isFetching: isFallbackFetching,
  } = useSearchProductsQuery(keyword, shouldUseFallback)

  const fallbackProducts: SearchResultProduct[] = fallbackData?.products ?? []
  const showingFallbackProducts =
    shouldUseFallback && fallbackProducts.length > 0
  const displayedProducts: SearchResultProduct[] = showingFallbackProducts
    ? fallbackProducts
    : hits

  const isLoading =
    ((status === 'loading' || status === 'stalled') && !hasAlgoliaHits) ||
    (shouldUseFallback && (isFallbackLoading || isFallbackFetching))

  if (isLoading && displayedProducts.length === 0) {
    return (
      <div className='grid auto-cols-[160px] grid-flow-col gap-4 overflow-x-auto md:auto-cols-fr md:grid-cols-3 lg:grid-cols-4'>
        {[...Array(4)].map((_, i) => (
          <div key={i} className='space-y-4'>
            <Skeleton className='aspect-[3/4] rounded-lg' />
            <Skeleton className='h-4 w-2/3' />
            <Skeleton className='h-4 w-1/2' />
          </div>
        ))}
      </div>
    )
  }

  if (
    shouldUseFallback &&
    !isFallbackLoading &&
    !isFallbackFetching &&
    fallbackProducts.length === 0
  ) {
    return (
      <>
        <div className='text-center text-sm italic text-neutral-700'>
          {t('title.no_result')}
        </div>
        {emptyContent}
        <TrackingDataLayer
          data={{
            event: ETrackingEvent.SearchNoResults,
            search_term: keyword,
          }}
        />
      </>
    )
  }

  if (
    !shouldUseFallback &&
    displayedProducts.length === 0 &&
    status === 'idle'
  ) {
    return null
  }

  return (
    <div>
      <h3 className='mb-4 text-base font-medium'>
        {t('header.search_bar.title.search_result')}
      </h3>
      <div className='grid auto-cols-[160px] grid-flow-col gap-4 overflow-x-auto md:auto-cols-fr md:grid-cols-3 lg:grid-cols-4'>
        {displayedProducts.map((product) => (
          <SearchProductCard
            key={product.objectID ?? product.seo_alias ?? product.slug}
            product={toSearchHighlightProduct(product)}
            title={
              isAlgoliaHitProduct(product) ? (
                <Highlight hit={product} attribute='title' />
              ) : undefined
            }
            onClick={() => {
              setTimeout(() => {
                setFalse()
                setIsOpen(false)
              }, 300)
            }}
          />
        ))}
      </div>
      {/* Loadmore button */}
      <div className='mt-4 flex justify-center md:mt-8'>
        <Button asChild variant='black'>
          <Link
            ref={viewAllRef}
            href={`/spotlight?search=${encodeURIComponent(keyword)}`}
            onClick={() => {
              // Lưu kết quả đang hiển thị để trang spotlight xếp lên đầu
              saveSearchHighlightProducts(
                keyword,
                displayedProducts.map(toSearchHighlightProduct)
              )
              setFalse()
              setTimeout(() => {
                setIsOpen(false)
              }, 300)
            }}
          >
            {t('button.view_all')}
          </Link>
        </Button>
      </div>
    </div>
  )
}

const QuerySuggestions = ({
  onSelectSuggestion,
}: {
  onSelectSuggestion: (query: string) => void
}) => {
  return (
    <Index
      indexName={
        process.env.NEXT_PUBLIC_ALGOLIA_SUGGESTION_INDEX ||
        'products_query_suggestions_new'
      }
    >
      <Configure hitsPerPage={5} />
      <SuggestionHits onSelectSuggestion={onSelectSuggestion} />
    </Index>
  )
}

const SuggestionHits = ({
  onSelectSuggestion,
}: {
  onSelectSuggestion: (query: string) => void
}) => {
  const { hits } = useHits()

  if (hits.length === 0) return null

  return (
    <div className='mb-4 md:mb-0'>
      <h3 className='mb-4 text-base font-medium'>Gợi ý tìm kiếm</h3>
      <div className='flex flex-wrap gap-2'>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {hits.map((hit: any) => (
          <Button
            key={hit.objectID}
            // Mobile: hover dính sau khi chạm làm badge vừa chọn bị nền đen
            className='whitespace-nowrap rounded-full border border-neutral-200 px-4 py-2 font-sans text-sm normal-case max-md:hover:bg-transparent max-md:hover:text-neutral-900'
            variant='outline-neutral'
            size='small'
            onClick={() => onSelectSuggestion(hit.query)}
            ga-tracking-value={`navbar__search__suggestion`}
            ga-tracking-label={`navbar__search__suggestion__${hit.query}`}
          >
            <SearchIcon className='mr-1.5 h-3.5 w-3.5 text-neutral-500' />
            <span className='[&_.ais-Highlight-highlighted]:bg-transparent [&_.ais-Highlight-highlighted]:font-bold [&_.ais-Highlight-highlighted]:text-black'>
              <Highlight hit={hit} attribute='query' />
            </span>
          </Button>
        ))}
      </div>
    </div>
  )
}
